import React, {
  type ChangeEvent,
  type KeyboardEvent,
  type LabelHTMLAttributes,
  useCallback,
  useRef,
} from 'react';

import PropTypes from 'prop-types';

import CloseButton from '@adsk/alloy-react-close-button';
import { useControlled, useInstanceId } from '@adsk/alloy-react-helpers';
import theme, {
  stylePropType,
  getInteractiveStyles,
  type StylableComponent,
} from '@adsk/alloy-react-theme';

type TagProps = StylableComponent<
  HTMLLabelElement,
  LabelHTMLAttributes<HTMLLabelElement>
> & {
  disabled?: boolean;
  defaultSelected?: boolean;
  selected?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onClose?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

/**
 * Tags are compact elements that can be used to represent small blocks of information.
 * They are descriptive words to either filter or identify the content, and can also be a good alternative to toggle switches.
 */
const Tag = React.forwardRef<HTMLInputElement, TagProps>(
  (
    {
      disabled = false,
      selected: selectedProp,
      defaultSelected = false,
      onChange: onChangeProp,
      onClose,
      children,
      className,
      style,
      ...props
    },
    ref,
  ) => {
    const closeRef = useRef<HTMLButtonElement>(null);

    const id = `alloy-tag-${useInstanceId()}`;

    const [selected, setSelected] = useControlled<boolean>(
      defaultSelected ?? false,
      selectedProp,
    );

    const onChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        if (!disabled) {
          onChangeProp && onChangeProp(event);
          setSelected(!selected);
        }
      },
      [disabled, onChangeProp, selected, setSelected],
    );

    const onKeyDown = useCallback(
      (event: KeyboardEvent<HTMLLabelElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          const input = document.getElementById(id) as HTMLInputElement;

          input.click();
        } else if (event.key === 'Backspace' || event.key === 'Delete') {
          closeRef.current?.click();
        }
      },
      [id],
    );

    return (
      // Will be fixed in https://jira.autodesk.com/browse/DSGN-1839
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
      <label
        // Will be fixed in https://jira.autodesk.com/browse/DSGN-1839
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        className={className}
        htmlFor={id}
        css={[
          {
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            padding: `0px ${theme.spacing.XS}px`,
            background: theme.tokens.colors.background.tertiary.value,
            color: disabled
              ? theme.tokens.colors.text.inactive.value
              : theme.tokens.colors.text.primary.value,
            borderRadius: theme.borderRadius.pill,
            border: `1px solid ${theme.tokens.colors.border.value}`,
            ...getInteractiveStyles(disabled),
            ...(!disabled && {
              ':hover': {
                borderColor: theme.tokens.colors.border.hover.value,
              },
              ':active': {
                borderColor: theme.tokens.colors.border.pressed.value,
              },
              ':focus': {
                borderColor: theme.tokens.colors.border.neutral.focused.value,
              },
            }),

            ...(selected && {
              background: theme.tokens.colors.background.selected.subtle.value,
              borderColor: theme.tokens.colors.border.selected.subtle.value,
            }),

            ...(disabled && {
              background: selected
                ? theme.tokens.colors.background.selected.subtle.value
                : theme.tokens.colors.background.neutral.inactive.value,
              borderColor: selected
                ? theme.tokens.colors.border.selected.subtle.value
                : theme.tokens.colors.border.inactive.value,
            }),
          },
          theme.typography.bodyMediumBold,
          style,
        ]}
        onKeyDown={onKeyDown}
        {...props}
      >
        <input
          id={id}
          ref={ref}
          value={selected.toString()}
          onChange={onChange}
          type="checkbox"
          checked={selected}
          disabled={disabled}
          css={[
            {
              display: 'none',
            },
          ]}
        />
        <span
          css={{
            cursor: 'inherit',
            padding: `${theme.spacing.XXS}px 0px`,
          }}
        >
          {children}
        </span>
        {onClose && (
          <CloseButton
            ref={closeRef}
            tabIndex={-1}
            disabled={disabled}
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.stopPropagation(); // prevent Tag selection
              onClose(e);
            }}
          />
        )}
      </label>
    );
  },
);

Tag.displayName = 'Tag';

Tag.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /**  Tag text content. */
  children: PropTypes.any,
  /** Whether Tag is disabled */
  disabled: PropTypes.bool,
  /** Whether Tag is selected when first rendered */
  defaultSelected: PropTypes.bool,
  /** Whether Tag is selected */
  selected: PropTypes.bool,
  /** Function executed upon (un)selecting tag */
  onChange: PropTypes.func,
  /** Function executed upon closing tag */
  onClose: PropTypes.func,
};

export default Tag;
